import { set } from 'browser-cookies';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { breakpoints } from '../../utils/styles';

// Swipe support
type TouchState = {
    startX: number | null;
    lastX: number | null;
    dragging: boolean;
};

function useSwipe(onSwipeLeft: () => void, onSwipeRight: () => void) {
    const touchState = useRef<TouchState>({ startX: null, lastX: null, dragging: false });

    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 1) {
            touchState.current.startX = e.touches[0].clientX;
            touchState.current.lastX = e.touches[0].clientX;
            touchState.current.dragging = true;
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!touchState.current.dragging || e.touches.length !== 1) return;
        touchState.current.lastX = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (!touchState.current.dragging || touchState.current.startX === null || touchState.current.lastX === null) return;
        const deltaX = touchState.current.lastX - touchState.current.startX;
        const threshold = 50; // px

        if (deltaX > threshold) {
            onSwipeRight();
        } else if (deltaX < -threshold) {
            onSwipeLeft();
        }
        touchState.current.startX = null;
        touchState.current.lastX = null;
        touchState.current.dragging = false;
    };

    return { handleTouchStart, handleTouchMove, handleTouchEnd };
}

const Container = styled.div<{ fitsAll: boolean }>`
    .carousel-wrapper {
        overflow: hidden;
        position: relative;
        width: 100%;
    }

    .carousel-container {
        display: flex;
        width: max-content;

        ${(props) => (props.fitsAll && `
            justify-content: center;
            min-width: 100%;
        `)}
    }

    .carousel-button {
        border: none;
        background: var(--page-bg);
        color: var(--text-primary);
        cursor: pointer;
        display: none;
        font-size: 1.5rem;
        height: 100%;
        padding: 1rem;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 10;

        &:focus {
            outline: none;
        }

        @media screen and (min-width: ${breakpoints.desktop}px) {
            display: flex;
            align-items: center;
        }
    }

    .carousel-button--left {
        left: 0;
    }

    .carousel-button--right {
        right: 0;
    }

    .carousel-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

interface CarouselProps {
    children: React.ReactNode[];
}

const Carousel: React.FC<CarouselProps> = ({ children }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null); // Nuevo ref para el wrapper
    const [currentOffset, setCurrentOffset] = useState(0);
    const [maxOffset, setMaxOffset] = useState(0);
    const [fitsAll, setFitsAll] = useState(true);
    const scrollLengthRem = 7;

    const calculateDimensions = useCallback(() => {
        if (!containerRef.current || !wrapperRef.current) return;

        const container = containerRef.current;
        const wrapper = wrapperRef.current;
        const containerWidthPx = Math.ceil(wrapper.getBoundingClientRect().width);
        const scrollWidthPx = Math.ceil(container.scrollWidth);
        const maxOffsetPx = Math.max(0, scrollWidthPx - containerWidthPx);
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const maxOffsetRem = maxOffsetPx / rootFontSize;
        const maxOffsetIn6RemSteps = Math.ceil(maxOffsetRem / scrollLengthRem) * scrollLengthRem;
        
        setFitsAll(scrollWidthPx <= containerWidthPx);
        setMaxOffset(maxOffsetIn6RemSteps);
    }, []);

    useEffect(() => {
        calculateDimensions();

        const handleResize = () => calculateDimensions();
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, [calculateDimensions, children]);

    const goLeft = useCallback(() => {
        setCurrentOffset(prev => Math.max(0, prev - scrollLengthRem));
    }, []);

    const goRight = useCallback(() => {
        setCurrentOffset(prev => Math.min(maxOffset, prev + scrollLengthRem));
    }, [maxOffset]);

    const { handleTouchStart, handleTouchMove, handleTouchEnd } = useSwipe(goRight, goLeft);

    const canGoLeft = currentOffset > 0;
    const canGoRight = currentOffset < maxOffset;

    return (
        <Container fitsAll={fitsAll}>
            <div ref={wrapperRef} className="carousel-wrapper" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
                {canGoLeft && (
                    <button
                        className="carousel-button carousel-button--left"
                        onClick={goLeft}
                    >
                        <i className="fa-light fa-arrow-left"></i>
                    </button>
                )}

                <div
                    ref={containerRef}
                    className="carousel-container"
                    style={{
                        transform: `translateX(-${currentOffset}rem)`,
                        transition: 'transform 0.3s ease'
                    }}
                >
                    {children}
                </div>

                {canGoRight && (
                    <button
                        className="carousel-button carousel-button--right"
                        onClick={goRight}
                    >
                        <i className="fa-light fa-arrow-right"></i>
                    </button>
                )}
            </div>
        </Container>
    );
};

export default Carousel;